import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import api from "../../utils/api";
import { errorAlert } from "../Alerts/actions";
import { fetchAiWallets } from "../AICredits/actions";
import {
  draftDocumentSuccess,
  draftDocumentFailure,
  cowriteSuccess,
  cowriteFailure,
  evidenceSearchSuccess,
  evidenceSearchFailure,
  evidenceDetailSuccess,
  evidenceDetailFailure,
  applyCowriteSuccess,
  applyCowriteFailure,
  refineFieldSuccess,
  refineFieldFailure,
} from "./actions";
import {
  DRAFT_DOCUMENT,
  COWRITE,
  EVIDENCE_SEARCH,
  EVIDENCE_DETAIL,
  APPLY_COWRITE,
  REFINE_FIELD,
  FETCH_AUTHORING_HISTORY,
} from "./constants";

export function* draftDocumentSaga({ payload, onSuccess, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, templateId, carePlanId, sectionId, locationId } =
    payload || {};

  try {
    // AI drafting can take several seconds — allow a generous timeout.
    const { data } = yield call(
      api(token).post,
      `/api/resident/${residentId}/document-draft`,
      {
        template_id: templateId,
        care_plan_id: carePlanId ?? null,
        section_id: sectionId ?? null,
      },
      { timeout: 300000 },
    );

    yield put(draftDocumentSuccess(data));

    // Drafting consumes care_planning credits — refresh the wallet so the
    // balance the rest of the app reads stays in sync.
    if (locationId) {
      yield put(fetchAiWallets({ location_id: locationId }));
    }

    if (onSuccess) onSuccess(data);
  } catch (e) {
    yield put(draftDocumentFailure(e));
    // Transport/server error (the out-of-credits path is a soft-fail 200 handled
    // by the caller, not here). Let the component show an inline message; only
    // fall back to a global alert when there's no handler.
    if (onError) {
      onError(e);
    } else if (typeof e === "string") {
      yield put(errorAlert(e));
    } else if (typeof e?.error === "string") {
      yield put(errorAlert(e.error));
    } else {
      yield put(errorAlert("Could not draft this document with AI."));
    }
  }
}

export function* cowriteSaga({ payload, onSuccess, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const {
    residentId,
    templateId,
    carePlanId,
    target,
    action,
    messages,
    locationId,
    // Informed-drafting extras (evidence panel). All optional — only forwarded
    // when the caller supplies them so legacy flows send byte-identical bodies.
    direction,
    evidenceReviewed,
    evidence,
    // Second turn of an ask-back — the model's questions are answered (or skipped)
    // and folded into `direction`. Tells the api not to offer another round.
    clarified,
    // Pre-bind wound selection: a brand-new wound plan drafted before its first
    // save has no wound BINDING yet — this is the only way the api can know
    // which wound(s) the nurse picked. Optional; omitted => byte-identical body.
    subjectIds,
  } = payload || {};

  try {
    const body = {
      template_id: templateId,
      care_plan_id: carePlanId ?? null,
      target: target ?? "document",
      action: action ?? "discuss",
      messages: Array.isArray(messages) ? messages : [],
    };
    // Include a key only when the caller passed it (undefined => omit) so the
    // existing draft/context/discuss requests are unchanged.
    if (direction !== undefined) body.direction = direction ?? null;
    if (Array.isArray(subjectIds) && subjectIds.length > 0) {
      body.subject_ids = subjectIds;
    }
    if (evidenceReviewed !== undefined) {
      body.evidence_reviewed = !!evidenceReviewed;
    }
    if (evidence !== undefined) body.evidence = evidence ?? null;
    if (clarified !== undefined) body.clarified = !!clarified;

    const { data } = yield call(
      api(token).post,
      `/api/resident/${residentId}/ai-cowrite`,
      body,
      { timeout: 300000 },
    );

    yield put(cowriteSuccess(data));

    // A draft turn spends a care_planning credit — refresh the wallet.
    if (action === "draft" && !data?.ai_unavailable && locationId) {
      yield put(fetchAiWallets({ location_id: locationId }));
    }

    if (onSuccess) onSuccess(data);
  } catch (e) {
    yield put(cowriteFailure(e));
    if (onError) {
      onError(e);
    } else if (typeof e === "string") {
      yield put(errorAlert(e));
    } else if (typeof e?.error === "string") {
      yield put(errorAlert(e.error));
    } else {
      yield put(errorAlert("Could not reach the AI co-writer."));
    }
  }
}

export function* evidenceSearchSaga({ payload, onSuccess, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, templateId, carePlanId, category, q, page } =
    payload || {};

  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident/${residentId}/ai-cowrite`,
      {
        template_id: templateId,
        care_plan_id: carePlanId ?? null,
        target: "document",
        action: "evidence_search",
        evidence: {
          search: {
            category: category ?? null,
            q: q ?? "",
            page: page ?? 1,
          },
        },
      },
    );

    // CustomJsonResponse wraps the payload under `.data`; unwrap it (mirrors the
    // authoring-history read) so callers get the rows/has_more object directly.
    const result = data?.data ?? data;

    yield put(evidenceSearchSuccess(result));
    if (onSuccess) onSuccess(result);
  } catch (e) {
    yield put(evidenceSearchFailure(e));
    // The panel keeps working with an inline note when a handler is supplied;
    // only fall back to a global alert when there isn't one.
    if (onError) {
      onError(e);
    } else if (typeof e === "string") {
      yield put(errorAlert(e));
    } else if (typeof e?.error === "string") {
      yield put(errorAlert(e.error));
    } else {
      yield put(errorAlert("Could not search for more evidence."));
    }
  }
}

export function* evidenceDetailSaga({ payload, onSuccess, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, templateId, carePlanId, kind, id } = payload || {};

  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident/${residentId}/ai-cowrite`,
      {
        template_id: templateId,
        care_plan_id: carePlanId ?? null,
        target: "document",
        action: "evidence_detail",
        evidence: {
          detail: {
            kind: kind ?? null,
            id: id ?? null,
          },
        },
      },
    );

    // CustomJsonResponse wraps the payload under `.data`; unwrap it (mirrors the
    // evidence-search read) so callers get the { action, detail } object directly.
    const result = data?.data ?? data;

    yield put(evidenceDetailSuccess(result));
    if (onSuccess) onSuccess(result);
  } catch (e) {
    yield put(evidenceDetailFailure(e));
    // The row shows a quiet "Couldn't load this item." when a handler is
    // supplied; only fall back to a global alert when there isn't one.
    if (onError) {
      onError(e);
    } else if (typeof e === "string") {
      yield put(errorAlert(e));
    } else if (typeof e?.error === "string") {
      yield put(errorAlert(e.error));
    } else {
      yield put(errorAlert("Could not load this evidence item."));
    }
  }
}

export function* applyCowriteSaga({ payload, onSuccess, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, carePlanId, target, schedule, tasks, equipment } =
    payload || {};

  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident/${residentId}/ai-cowrite/apply`,
      {
        care_plan_id: carePlanId ?? null,
        target: target ?? "schedule",
        schedule: Array.isArray(schedule) ? schedule : [],
        tasks: Array.isArray(tasks) ? tasks : [],
        equipment: Array.isArray(equipment) ? equipment : [],
      },
    );

    yield put(applyCowriteSuccess(data));
    if (onSuccess) onSuccess(data);
  } catch (e) {
    yield put(applyCowriteFailure(e));
    if (onError) {
      onError(e);
    } else if (typeof e === "string") {
      yield put(errorAlert(e));
    } else if (typeof e?.error === "string") {
      yield put(errorAlert(e.error));
    } else {
      yield put(errorAlert("Could not apply the schedules & tasks."));
    }
  }
}

export function* refineFieldSaga({ payload, onSuccess, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const {
    residentId,
    templateId,
    carePlanId,
    fieldType,
    value,
    fieldLabel,
    sectionTitle,
    planContext,
    locationId,
    itemIndex,
    pointer,
  } = payload || {};

  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident/${residentId}/ai-refine`,
      {
        template_id: templateId,
        care_plan_id: carePlanId ?? null,
        field_type: fieldType === "list" ? "list" : "text",
        value: value ?? (fieldType === "list" ? [] : ""),
        field_label: fieldLabel ?? null,
        section_title: sectionTitle ?? null,
        plan_context: planContext ?? null,
        item_index: itemIndex ?? null,
        // Her short instruction ("add the stand-aid at night"). Omitted when blank,
        // so a plain tidy request is unchanged.
        ...(typeof pointer === "string" && pointer.trim()
          ? { pointer: pointer.trim() }
          : {}),
      },
      { timeout: 120000 },
    );

    yield put(refineFieldSuccess(data));

    // A successful refine spends a care_planning credit — refresh the wallet.
    if (!data?.ai_unavailable && locationId) {
      yield put(fetchAiWallets({ location_id: locationId }));
    }

    if (onSuccess) onSuccess(data);
  } catch (e) {
    yield put(refineFieldFailure(e));
    // Out-of-credits is a soft-fail 200 handled by the caller; this is a
    // transport/server error. Let the caller show an inline note.
    if (onError) {
      onError(e);
    } else if (typeof e === "string") {
      yield put(errorAlert(e));
    } else if (typeof e?.error === "string") {
      yield put(errorAlert(e.error));
    } else {
      yield put(errorAlert("Could not refine this text with AI."));
    }
  }
}

export function* fetchAuthoringHistorySaga({ payload, onSuccess, onError }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, templateId } = payload || {};

  try {
    const { data } = yield call(
      api(token).get,
      `/api/resident/${residentId}/ai-authoring-history?template_id=${encodeURIComponent(
        templateId,
      )}`,
    );
    const events = Array.isArray(data) ? data : (data?.data ?? []);
    if (onSuccess) onSuccess(events);
  } catch (e) {
    // Read-only; a failure just leaves the rail section hidden. No global alert.
    if (onError) onError(e);
  }
}

export default function* DocumentDraftSaga() {
  yield all([
    takeLatest(DRAFT_DOCUMENT, draftDocumentSaga),
    takeLatest(COWRITE, cowriteSaga),
    // Separate watcher from COWRITE so a search can't cancel an in-flight draft.
    takeLatest(EVIDENCE_SEARCH, evidenceSearchSaga),
    // Own watcher too — rapid taps on different rows cancel earlier detail fetches
    // (only the latest expansion loads); a draft/search is never disturbed.
    takeLatest(EVIDENCE_DETAIL, evidenceDetailSaga),
    takeLatest(APPLY_COWRITE, applyCowriteSaga),
    // takeEvery, not takeLatest — refining field B must not cancel field A.
    takeEvery(REFINE_FIELD, refineFieldSaga),
    takeLatest(FETCH_AUTHORING_HISTORY, fetchAuthoringHistorySaga),
  ]);
}
